<script setup lang="ts">
import type { PacProduct } from '@/models/pacJobBooking';

import KNumberDecimalField from '@/components/fields/KNumberDecimalField.vue';
import KNumberStepperField from '@/components/fields/KNumberStepperField.vue';
import KSelectField from '@/components/fields/KSelectField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import {
  PAC_PACKAGE_OPTIONS,
  PAC_PRODUCT_OPTIONS,
  createEmptyPacPalletProduct,
} from '@/models/pacJobBooking';

export interface Props {
  isDeletable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isDeletable: false,
});

const emit = defineEmits<{
  deleteBtnClick: [event: Event];
}>();

const model = defineModel<PacProduct>({
  default: () => createEmptyPacPalletProduct(),
});

function handleProduct(
  value: string | number | boolean | null | undefined,
): void {
  model.value = { ...model.value, product: value ? String(value) : null };
}

function handleWeight(value: number | null | undefined): void {
  model.value = { ...model.value, weight: value ?? 0 };
}

function handleQuantity(value: number): void {
  model.value = { ...model.value, quantity: value };
}

function handleDescription(value: string | null | undefined): void {
  model.value = { ...model.value, description: value ?? '' };
}

function handleDeleteClick(event: Event): void {
  emit('deleteBtnClick', event);
}
</script>

<template>
  <div :class="$style.pacPalletProductRow" data-testid="pacPalletProductRow">
    <KSelectField
      :model-value="model.box_type"
      label="Box type"
      size="md"
      :items="PAC_PACKAGE_OPTIONS"
      readonly
    />
    <KSelectField
      :model-value="model.product"
      label="Product"
      size="md"
      :items="PAC_PRODUCT_OPTIONS"
      @update:model-value="handleProduct"
    />
    <KNumberDecimalField
      :model-value="model.weight"
      label="Weight (kg)"
      size="md"
      @update:model-value="handleWeight"
    />
    <KNumberStepperField
      :model-value="model.quantity"
      :min="1"
      size="md"
      @update:model-value="handleQuantity"
    />
    <KTextField
      :model-value="model.description"
      label="Description"
      size="md"
      @update:model-value="handleDescription"
    />
    <v-btn
      v-if="props.isDeletable"
      icon="mdi-trash-can-outline"
      size="x-small"
      variant="text"
      color="error"
      aria-label="Delete product"
      :class="$style.deleteBtn"
      @click="handleDeleteClick"
    />
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

.pacPalletProductRow {
  display: grid;
  grid-template-columns: minmax(160px, 1.2fr) minmax(180px, 1.4fr) minmax(120px, 0.8fr) auto minmax(160px, 1.4fr) auto;
  gap: 16px;
  align-items: start;
  padding: 16px;
  border-bottom: 1px solid colors.$color-gray-100;

  &:last-child {
    border-bottom: none;
  }
}

.deleteBtn {
  margin-top: 4px;
}
</style>
